import { PushNotifications, Channel } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

/**
 * Creates a default notification channel for Android.
 * Required for Android 8.0 (API 26+) to display notifications.
 * If the backend doesn't specify channel_id, this default channel will be used.
 */
export const createDefaultNotificationChannel = async (): Promise<void> => {
	// Only needed for Android
	if (Capacitor.getPlatform() !== 'android') {
		return;
	}

	try {
		const channel: Channel = {
			id: 'default',
			name: 'Default Notifications',
			description: 'Default notification channel for app alerts',
			importance: 4, // High importance: makes a sound and appears as a heads-up notification
			visibility: 1, // Public: shows full notification content on lock screen
			sound: 'default', // Use default notification sound
			vibration: true, // Enable vibration
			lights: true, // Enable notification LED
			lightColor: '#3880FF' // Blue light color
		};

		await PushNotifications.createChannel(channel);
	} catch {
		// Silently fail - channel creation is not critical for app function
	}
};

/**
 * Deletes all notification channels (useful for testing/debugging).
 * Be careful using this in production as it removes user channel preferences.
 */
export const deleteAllNotificationChannels = async (): Promise<void> => {
	if (Capacitor.getPlatform() !== 'android') {
		return;
	}

	try {
		const channels = await PushNotifications.listChannels();
		await Promise.all(channels.channels.map((channel) => PushNotifications.deleteChannel({ id: channel.id })));
	} catch {
		// Silently fail
	}
};

/**
 * Lists all existing notification channels.
 * Returns the channels array or empty array on error.
 */
export const listNotificationChannels = async (): Promise<Channel[]> => {
	if (Capacitor.getPlatform() !== 'android') {
		return [];
	}

	try {
		const result = await PushNotifications.listChannels();
		return result.channels;
	} catch {
		return [];
	}
};
